import { RAG_API_URL, getAuthToken } from '@/config/env'

export interface ElevenLabsAgent {
  agent_id: string
  name: string
  tags?: string[]
  created_at_unix_secs?: number
  access_info?: {
    role?: string
    creator_name?: string
  }
}

export interface ElevenLabsDocument {
  id: string
  name?: string
  type?: string
}

export interface ElevenLabsAgentKnowledgeBase {
  agent_id: string
  remote_document_ids: string[]
  local_document_ids: number[]
  local_documents?: Array<{
    id: number
    remote_document_id?: string
    title?: string
    document_type?: string | null
  }>
  orphaned_remote_document_ids: string[]
  reconciled_removed_remote_document_ids?: string[]
  reconciled_removed_unlinked_remote_document_ids?: string[]
}

export interface ElevenLabsAgentPromptConfig {
  agent_id: string
  prompt: string
  first_message: string
}

export interface ElevenLabsAgentsResponse {
  items: ElevenLabsAgent[]
  count?: number
  constraints?: {
    max_file_bytes?: number
    allowed_local_document_types?: string[]
    allowed_upload_extensions?: string[]
  }
}

class ElevenLabsAgentsService {
  private baseUrl = RAG_API_URL
  private integrationsBase = '/api/v1/rag/integrations/elevenlabs'

  private unwrapData<T>(response: { success?: boolean; data?: T }): T {
    if (response && response.success && response.data) {
      return response.data
    }
    return (response?.data as T) ?? (response as unknown as T)
  }

  async listAgents(): Promise<ElevenLabsAgentsResponse> {
    const token = getAuthToken()
    const response = await fetch(`${this.baseUrl}${this.integrationsBase}/agents/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const payload = await response.json()
    return this.unwrapData<ElevenLabsAgentsResponse>(payload)
  }

  async createDocumentFromUrl(payload: {
    url: string
    title?: string
    description?: string
    metadata?: Record<string, unknown>
  }): Promise<{ document: ElevenLabsDocument }> {
    const token = getAuthToken()
    const response = await fetch(`${this.baseUrl}${this.integrationsBase}/documents/url/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const data = await response.json()
    return this.unwrapData<{ document: ElevenLabsDocument }>(data)
  }

  async createDocumentFromFile(payload: {
    file: File
    title?: string
    description?: string
    metadata?: Record<string, unknown>
  }): Promise<{ document: ElevenLabsDocument }> {
    const token = getAuthToken()
    const formData = new FormData()
    formData.append('file', payload.file)
    if (payload.title) formData.append('title', payload.title)
    if (payload.description) formData.append('description', payload.description)
    if (payload.metadata) formData.append('metadata', JSON.stringify(payload.metadata))

    const response = await fetch(`${this.baseUrl}${this.integrationsBase}/documents/file/`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: formData,
    })

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const data = await response.json()
    return this.unwrapData<{ document: ElevenLabsDocument }>(data)
  }

  async assignDocuments(agentId: string, remoteDocumentIds: string[]): Promise<void> {
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/knowledge-base/assign/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ remote_document_ids: remoteDocumentIds }),
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }
  }

  async unassignDocuments(agentId: string, remoteDocumentIds: string[]): Promise<void> {
    if (!remoteDocumentIds.length) return
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/knowledge-base/unassign/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ remote_document_ids: remoteDocumentIds }),
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }
  }

  async syncAndAssignLocalDocuments(agentId: string, documentIds: number[]): Promise<void> {
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/knowledge-base/sync/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          document_ids: documentIds,
          dry_run: false,
          only_embedded: true,
          verify_remote: true,
        }),
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }
  }

  async getAgentPromptConfig(agentId: string): Promise<ElevenLabsAgentPromptConfig> {
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/prompt/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const payload = await response.json()
    return this.unwrapData<ElevenLabsAgentPromptConfig>(payload)
  }

  async updateAgentPromptConfig(
    agentId: string,
    payload: { prompt?: string; first_message?: string }
  ): Promise<ElevenLabsAgentPromptConfig> {
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/prompt/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const data = await response.json()
    return this.unwrapData<ElevenLabsAgentPromptConfig>(data)
  }

  async getAgentKnowledgeBase(agentId: string): Promise<ElevenLabsAgentKnowledgeBase> {
    const token = getAuthToken()
    const response = await fetch(
      `${this.baseUrl}${this.integrationsBase}/agents/${agentId}/knowledge-base/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    )

    if (!response.ok) {
      const message = await response.text()
      throw new Error(message || `HTTP ${response.status}`)
    }

    const payload = await response.json()
    return this.unwrapData<ElevenLabsAgentKnowledgeBase>(payload)
  }
}

export const elevenlabsAgentsService = new ElevenLabsAgentsService()
